import type { Controls } from "@obean/game-contract";
import { useEffect, useRef, useState } from "preact/hooks";
import type { ControlButtons } from "./controls.js";
import { Button, stickVector } from "./controls.js";
import type { Hand } from "./hand.js";
import { barSlots, hand, setHand } from "./hand.js";

/** Full stick deflection at this many CSS pixels from where the thumb landed. */
const STICK_RADIUS = 56;

/** Touch state shared with the input sampler (written by the overlay, read once per step). */
export class TouchInput {
  moveX = 0;
  moveY = 0;
  buttons = 0;
  /** Buttons pressed since the last sample: a quick tap counts once even if it's already up. */
  #tapped = 0;
  /** Swipe steps not yet sent (ADR 0023): negative left, positive right. */
  #steps = 0;
  /** The last sample was a step: the next is a rest, so the game sees each step as a new press. */
  #stepped = false;

  /** The buttons held now (the touch controls set them as fingers go down and up). */
  setButtons(buttons: number): void {
    this.#tapped |= buttons & ~this.buttons;
    this.buttons = buttons;
  }

  /** A button pressed and let go at once (a swipe's tap or flick): counted once, at the next sample. */
  tap(button: number): void {
    this.#tapped |= button;
  }

  /** A swipe moved a finger-width left (-1) or right (+1): one step each, never lost or merged. */
  step(direction: -1 | 1): void {
    this.#steps += direction;
  }

  sample(): Controls {
    const buttons = this.buttons | this.#tapped;
    this.#tapped = 0;
    // Steps go out one per sample with a rest between, so a game that moves on a press (not a
    // hold) moves exactly once per step, however fast the finger went.
    if (this.#steps !== 0 && !this.#stepped) {
      const dir = Math.sign(this.#steps);
      this.#steps -= dir;
      this.#stepped = true;
      return { moveX: dir, moveY: 0, buttons };
    }
    const rest = this.#stepped;
    this.#stepped = false;
    return { moveX: rest ? 0 : this.moveX, moveY: rest ? 0 : this.moveY, buttons };
  }
}

/** Swipe gestures (ADR 0023): one step per this many CSS pixels of sideways drag. */
export const SWIPE_STEP = 30;
/** A touch shorter and stiller than this is a tap. */
const TAP_MS = 250;
const TAP_SLOP = 12;
/** Dragging this far down (more down than sideways) pushes down while the finger stays. */
const PUSH_DOWN = 36;
/** A flick: at least this far down, this fast (CSS pixels per millisecond). */
const FLICK_DOWN = 50;
const FLICK_SPEED = 0.6;

interface Swipe {
  id: number;
  start: { x: number; y: number; t: number };
  /** Where the last step was counted from, sideways. */
  fromX: number;
  moved: boolean;
}

/** The gestures in the game's own words: "Drag to move · tap to turn · flick down to drop". */
export function swipeHint(buttons: ControlButtons): string {
  const label = (b: string) => buttons.find((x) => x.button === b)?.label.toLowerCase();
  const tap = label("primary");
  const flick = label("secondary");
  return [
    "Drag to move",
    ...(tap ? [`tap to ${tap}`] : []),
    ...(flick ? [`flick down to ${flick}`] : []),
  ].join(" · ");
}

/**
 * Swipe controls (ADR 0023): gestures on the game itself, and its buttons in a bar underneath, so
 * no button covers the board. Drag sideways: a step per SWIPE_STEP pixels. Drag down and hold:
 * push down. Tap: `primary`. Flick down: `secondary`.
 */
function SwipeControls({ touch, buttons }: { touch: TouchInput; buttons: ControlButtons }) {
  const swipe = useRef<Swipe | null>(null);
  const zone = useRef<HTMLDivElement>(null);
  const [pressed, setPressed] = useState(0);
  useEffect(() => {
    touch.setButtons(pressed);
  }, [pressed, touch]);

  const onDown = (e: PointerEvent) => {
    if (swipe.current) return; // one finger gestures
    capture(zone.current, e.pointerId);
    swipe.current = {
      id: e.pointerId,
      start: { x: e.clientX, y: e.clientY, t: e.timeStamp },
      fromX: e.clientX,
      moved: false,
    };
  };
  const onMove = (e: PointerEvent) => {
    const s = swipe.current;
    if (s?.id !== e.pointerId) return;
    const dx = e.clientX - s.start.x;
    const dy = e.clientY - s.start.y;
    if (Math.hypot(dx, dy) > TAP_SLOP) s.moved = true;
    // Pushing down: well down, and more down than sideways. Sideways steps pause meanwhile.
    const down = dy > PUSH_DOWN && dy > Math.abs(dx);
    touch.moveY = down ? 1 : 0;
    if (down) return;
    let from = s.fromX;
    while (e.clientX - from >= SWIPE_STEP) {
      touch.step(1);
      from += SWIPE_STEP;
    }
    while (from - e.clientX >= SWIPE_STEP) {
      touch.step(-1);
      from -= SWIPE_STEP;
    }
    s.fromX = from;
  };
  const onUp = (e: PointerEvent) => {
    const s = swipe.current;
    if (s?.id !== e.pointerId) return;
    swipe.current = null;
    touch.moveY = 0;
    const dt = e.timeStamp - s.start.t;
    const dy = e.clientY - s.start.y;
    if (!s.moved && dt < TAP_MS) touch.tap(Button.primary);
    else if (dy > FLICK_DOWN && dy / Math.max(1, dt) > FLICK_SPEED) touch.tap(Button.secondary);
  };
  const onCancel = (e: PointerEvent) => {
    if (swipe.current?.id !== e.pointerId) return;
    swipe.current = null;
    touch.moveY = 0;
  };

  return (
    <div class="touch-controls swipe">
      <div
        ref={zone}
        class="swipe-zone"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onCancel}
      />
      <p class="swipe-hint">{swipeHint(buttons)}</p>
      <ButtonBar buttons={buttons} pressed={pressed} setPressed={setPressed} />
    </div>
  );
}

/**
 * The game's buttons in a bar under the game (ADR 0023), under the tapping thumb (ADR 0017): an
 * equal slot each, one free slot on the far side.
 */
function ButtonBar({
  buttons,
  pressed,
  setPressed,
}: {
  buttons: ControlButtons;
  pressed: number;
  setPressed: (update: (p: number) => number) => void;
}) {
  const side = hand();
  if (buttons.length === 0) return null;
  return (
    <div class={`swipe-bar ${side}`}>
      {barSlots(buttons, side).map((b, i) => {
        if (!b) return <span key={`free-${String(i)}`} class="bar-free" aria-hidden="true" />;
        const bit = Button[b.button];
        return (
          <button
            key={b.button}
            type="button"
            class={`touch-button bar${pressed & bit ? " pressed" : ""}`}
            aria-label={b.label}
            onPointerDown={(e) => {
              capture(e.currentTarget, e.pointerId);
              setPressed((p) => p | bit);
            }}
            onPointerUp={() => {
              setPressed((p) => p & ~bit);
            }}
            onPointerCancel={() => {
              setPressed((p) => p & ~bit);
            }}
          >
            {b.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Tap controls (ADR 0024): the game reads taps on its own canvas, so nothing lies over it; only its
 * buttons, in the bar.
 */
function TapControls({ touch, buttons }: { touch: TouchInput; buttons: ControlButtons }) {
  const [pressed, setPressed] = useState(0);
  useEffect(() => {
    touch.setButtons(pressed);
  }, [pressed, touch]);
  // No buttons (a quiz: the answers are the controls, ADR 0027): no bar, the game has the screen.
  if (buttons.length === 0) return null;
  return (
    <div class="touch-controls tap">
      <ButtonBar buttons={buttons} pressed={pressed} setPressed={setPressed} />
    </div>
  );
}

/** Keep receiving this pointer's moves even when the finger slides off the element. */
function capture(element: Element | null, pointerId: number): void {
  try {
    element?.setPointerCapture(pointerId);
  } catch {
    // The pointer already ended; nothing to capture.
  }
}

interface StickState {
  id: number;
  origin: { x: number; y: number };
  thumb: { x: number; y: number };
}

/** The primary button is the big one under the right thumb; the others sit beside it. */
const BUTTON_CLASS = {
  primary: "fire",
  secondary: "ability secondary",
  ability: "ability",
} as const;

/**
 * Touch controls: the move stick and the buttons the game declares (ADR 0017), in its order; or,
 * for a game that declares `touch: "swipe"`, gestures and a button bar (ADR 0023).
 */
export function TouchControls({
  touch,
  buttons,
  mode = "stick",
}: {
  touch: TouchInput;
  buttons: ControlButtons;
  mode?: "stick" | "swipe" | "tap";
}) {
  return mode === "swipe" ? (
    <SwipeControls touch={touch} buttons={buttons} />
  ) : mode === "tap" ? (
    <TapControls touch={touch} buttons={buttons} />
  ) : (
    <StickControls touch={touch} buttons={buttons} />
  );
}

/**
 * Stick controls in a dock beside the game, never over it (founder, 2026-10-29: "make the move
 * circle outside of the screen, so it does not block the game area"): along the bottom on a phone
 * held upright, a column each side when it's turned. The stick takes one side of the dock (it
 * appears where the thumb lands in it; until then a ring shows where), the buttons the tapping
 * thumb's side (src/input/hand.ts), and a small button swaps the sides there and then.
 * Multi-touch: steer and fire at once. Every target is well over 44 px (P2-WEB-05).
 */
function StickControls({ touch, buttons }: { touch: TouchInput; buttons: ControlButtons }) {
  const [side, setSide] = useState<Hand>(hand);
  const [stick, setStick] = useState<StickState | null>(null);
  const [pressed, setPressed] = useState(0);
  const zone = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const vector = stick
      ? stickVector(stick.origin, stick.thumb, STICK_RADIUS)
      : { moveX: 0, moveY: 0 };
    touch.moveX = vector.moveX;
    touch.moveY = vector.moveY;
    touch.setButtons(pressed);
  }, [stick, pressed, touch]);

  const onStickDown = (e: PointerEvent) => {
    if (stick) return; // one thumb steers
    capture(zone.current, e.pointerId);
    const point = { x: e.clientX, y: e.clientY };
    setStick({ id: e.pointerId, origin: point, thumb: point });
  };
  // The stick is drawn inside its zone: page coordinates less the zone's corner.
  const box = zone.current?.getBoundingClientRect();
  const at = (p: { x: number; y: number }) => ({
    x: p.x - (box?.left ?? 0),
    y: p.y - (box?.top ?? 0),
  });
  const swap = () => {
    const next = side === "right" ? "left" : "right";
    setHand(next);
    setSide(next);
    setStick(null);
    setPressed(0);
  };
  const onStickMove = (e: PointerEvent) => {
    if (stick?.id !== e.pointerId) return;
    setStick({ ...stick, thumb: { x: e.clientX, y: e.clientY } });
  };
  const onStickUp = (e: PointerEvent) => {
    if (stick?.id === e.pointerId) setStick(null);
  };

  const button = (bit: number, label: string, className: string) => (
    <button
      type="button"
      class={`touch-button ${className}${pressed & bit ? " pressed" : ""}`}
      aria-label={label}
      onPointerDown={(e) => {
        capture(e.currentTarget, e.pointerId);
        setPressed((p) => p | bit);
      }}
      onPointerUp={() => {
        setPressed((p) => p & ~bit);
      }}
      onPointerCancel={() => {
        setPressed((p) => p & ~bit);
      }}
    >
      {label}
    </button>
  );

  return (
    <div class={`touch-controls stick-dock ${side}`}>
      <div
        ref={zone}
        class="stick-zone"
        onPointerDown={onStickDown}
        onPointerMove={onStickMove}
        onPointerUp={onStickUp}
        onPointerCancel={onStickUp}
      >
        {!stick && (
          <span class="stick-base idle" aria-hidden="true">
            Move
          </span>
        )}
        {stick && (
          <>
            <span
              class="stick-base"
              style={{ left: at(stick.origin).x, top: at(stick.origin).y }}
            />
            <span
              class="stick-thumb"
              style={{
                left:
                  at(stick.origin).x +
                  stickVector(stick.origin, stick.thumb, STICK_RADIUS).moveX * STICK_RADIUS,
                top:
                  at(stick.origin).y +
                  stickVector(stick.origin, stick.thumb, STICK_RADIUS).moveY * STICK_RADIUS,
              }}
            />
          </>
        )}
      </div>
      {buttons.length > 0 && (
        <div class="touch-buttons">
          {/* From the thumb's edge inward (ADR 0017): the first button sits at the edge. */}
          {(side === "right" ? [...buttons].reverse() : buttons).map((b) =>
            button(Button[b.button], b.label, BUTTON_CLASS[b.button]),
          )}
        </div>
      )}
      <button
        type="button"
        class="swap-hand"
        aria-label={side === "right" ? "Steer on the right" : "Steer on the left"}
        title="Swap sides"
        onClick={swap}
      >
        ⇄
      </button>
    </div>
  );
}
